import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const PROMO_IMAGE = "https://customer-assets.emergentagent.com/job_4bd83fc7-85f3-4f83-a449-c9bd32029c80/artifacts/q3ucjn1u_8722cd25-f6fb-4fa0-843c-595a910aaf29.jpeg";

const STATIC_IMAGES = [
  { src: "/ysl-bags.jpg", alt: "Pink Saint Laurent YSL Tote Bags", span: "col-span-1" },
  { src: "/gucci-bags.jpg", alt: "Gucci GG Supreme Handbags Collection", span: "col-span-1 md:col-span-2" },
  { src: PROMO_IMAGE, alt: "Golden Egg Mystery Scoop Reveal", span: "col-span-1 md:col-span-2" },
  { src: "/gallery-1837.jpg", alt: "Luxury Mystery Scoop Reveal", span: "col-span-1" },
  { src: "/gallery-1834.jpg", alt: "Designer Accessories Collection", span: "col-span-1" },
  { src: "/gallery-1830.jpg", alt: "Luxury Gift Unboxing", span: "col-span-1" },
  { src: "/gallery-1823.jpg", alt: "Designer Purse Reveal", span: "col-span-1" },
  { src: "/gallery-1821.jpg", alt: "Golden Egg Treasures", span: "col-span-1 md:col-span-2" },
  { src: "/gallery-1906.jpg", alt: "Prada Milano Designer Handbag", span: "col-span-1" },
  { src: "/gallery-1908.jpg", alt: "Black Suede Bag with Gold Accessories", span: "col-span-1" },
  { src: "/gallery-1907.jpg", alt: "Gold Jewelry & Necklaces Collection", span: "col-span-1" },
  { src: "/gallery-1909.jpg", alt: "Pink Pearl Earrings & Necklace", span: "col-span-1 md:col-span-2" },
  { src: "/gallery-1831.jpg", alt: "Luxury Mystery Scoop Collection", span: "col-span-1" },
  { src: "/gallery-1816.jpg", alt: "Designer Luxury Collection", span: "col-span-1" },
  { src: "/gallery-1811.jpg", alt: "Luxury Accessories Reveal", span: "col-span-1" },
  { src: "/gallery-1808.jpg", alt: "Designer Treasure Display", span: "col-span-1 md:col-span-2" },
  { src: "/gallery-1809.jpg", alt: "Mystery Scoop Luxury Items", span: "col-span-1" },
  { src: "/gallery-1810.jpg", alt: "Exclusive Designer Finds", span: "col-span-1" },
  { src: "/gallery-1803.jpg", alt: "Luxury Designer Handbag", span: "col-span-1" },
  { src: "/gallery-1807.jpg", alt: "Designer Accessories Set", span: "col-span-1 md:col-span-2" },
  { src: "/gallery-1804b.jpg", alt: "Luxury Purse Collection", span: "col-span-1" },
  { src: "/gallery-1799.jpg", alt: "Designer Treasure Reveal", span: "col-span-1" },
  { src: "/gallery-1795.jpg", alt: "Luxury Mystery Gift", span: "col-span-1" },
];

// Assign spans in a repeating pattern for admin images
const SPAN_PATTERN = [
  "col-span-1", "col-span-1 md:col-span-2", "col-span-1",
  "col-span-1", "col-span-1", "col-span-1 md:col-span-2",
];

export default function Gallery() {
  const [images, setImages] = useState(STATIC_IMAGES);

  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/gallery`).then(({ data }) => {
      if (!cancelled && data.length > 0) {
        const adminImages = data.map((img, i) => ({
          src: `${BACKEND_URL}${img.url}`,
          alt: img.original_name || "Gallery Image",
          span: SPAN_PATTERN[i % SPAN_PATTERN.length],
        }));
        setImages([...adminImages, ...STATIC_IMAGES]);
      }
    }).catch(() => {});
    return () => { cancelled = true; };
  }, []);

  return (
    <section id="gallery" data-testid="gallery-section" className="py-24 md:py-32 relative">
      <div className="absolute inset-0 section-glow-gold" />
      <div className="max-w-7xl mx-auto px-6 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-20"
        >
          <p className="font-great-vibes text-[#FF007F] text-2xl md:text-3xl mb-4">The Treasury</p>
          <h2 className="font-playfair text-[#D4AF37] text-4xl md:text-5xl font-bold">Gallery & Past Reveals</h2>
          <div className="gold-line w-24 mx-auto mt-6" />
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {images.map((img, i) => (
            <motion.div
              key={`${img.src}-${i}`}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "200px" }}
              transition={{ duration: 0.6, delay: Math.min(i * 0.08, 0.5) }}
              className={`${img.span} group rounded-xl overflow-hidden transition-all duration-500 hover:shadow-[0_0_30px_rgba(212,175,55,0.15)]`}
              data-testid={`gallery-image-${i}`}
            >
              <div className="relative bg-[#0f0f0f] p-3 rounded-xl border border-[#D4AF37]/20 hover:border-[#D4AF37]/60 transition-colors duration-500">
                <div className="overflow-hidden rounded-lg relative">
                  <img
                    src={img.src}
                    alt={img.alt}
                    className="w-full h-64 md:h-72 object-cover transition-transform duration-700 group-hover:scale-105"
                    style={{ filter: 'contrast(1.1) saturate(0.75) brightness(0.85)' }}
                  />
                  <div className="absolute inset-0 rounded-lg pointer-events-none" style={{
                    background: 'radial-gradient(ellipse at center, transparent 25%, rgba(10,10,10,0.5) 65%, rgba(10,10,10,0.85) 100%)'
                  }} />
                  <div className="absolute inset-0 rounded-lg pointer-events-none" style={{
                    background: 'linear-gradient(to bottom, rgba(212,175,55,0.04) 0%, transparent 20%, transparent 60%, rgba(10,10,10,0.7) 100%)'
                  }} />
                </div>
                <div className="absolute top-3 left-3 w-6 h-6 border-t-2 border-l-2 border-[#D4AF37]/30 rounded-tl-lg group-hover:border-[#D4AF37]/70 transition-colors duration-500" />
                <div className="absolute top-3 right-3 w-6 h-6 border-t-2 border-r-2 border-[#D4AF37]/30 rounded-tr-lg group-hover:border-[#D4AF37]/70 transition-colors duration-500" />
                <div className="absolute bottom-3 left-3 w-6 h-6 border-b-2 border-l-2 border-[#D4AF37]/30 rounded-bl-lg group-hover:border-[#D4AF37]/70 transition-colors duration-500" />
                <div className="absolute bottom-3 right-3 w-6 h-6 border-b-2 border-r-2 border-[#D4AF37]/30 rounded-br-lg group-hover:border-[#D4AF37]/70 transition-colors duration-500" />
              </div>
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "200px" }}
          transition={{ duration: 0.8 }}
          className="mt-10"
        >
          <div className="max-w-2xl mx-auto rounded-xl overflow-hidden border border-[#D4AF37]/30 hover:border-[#D4AF37] transition-colors duration-500">
            <video data-testid="gallery-video" controls autoPlay muted loop playsInline className="w-full">
              <source src="/mystery-reveal.mp4" type="video/mp4" />
            </video>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.3 }}
          className="mt-16 text-center"
        >
          <p className="font-montserrat text-white/30 text-xs tracking-[0.3em] uppercase mb-4">Designer-Inspired Collections</p>
          <div className="flex flex-wrap justify-center gap-x-8 gap-y-3">
            {["Louis Vuitton", "Gucci", "Chanel", "Dior", "YSL", "Hermes", "Prada", "Fendi"].map((brand) => (
              <span key={brand} className="font-playfair text-[#D4AF37]/40 text-lg md:text-xl italic">{brand}</span>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
}
